import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

const POS_COLOR = { 1: "#16FF7A", 2: "#FFCC00", 3: "#FF9F0A" };
const posColor = (p) => POS_COLOR[p] || "rgba(255,255,255,0.75)";

/**
 * Ponto verde ao lado do atleta: indica confronto prévio na competição.
 * Clique abre um painel (portal) com mini-tabela dos atletas repetidos e suas colocações.
 * Props: confrontos [{ category_id, categoria, positions:[{athlete_id, nome, posicao}] }], currentAthleteId, testId
 */
export default function ConfrontoIndicator({ confrontos, currentAthleteId, testId }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!confrontos || confrontos.length === 0) return null;

  const toggle = (e) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const width = 340;
      setPos({ top: r.bottom + 8, left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)) });
    }
    setOpen((o) => !o);
  };

  return (
    <span className="relative inline-flex items-center align-middle ml-1.5" data-testid={testId || "confronto-indicator"}>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        aria-label={`Confronto prévio: ${confrontos.length}`}
        aria-expanded={open}
        data-testid={`${testId || "confronto-indicator"}-dot`}
        className="w-2.5 h-2.5 rounded-full inline-block cursor-pointer"
        style={{
          background: "#16FF7A",
          boxShadow: "0 0 8px rgba(22,255,122,0.9), 0 0 2px rgba(22,255,122,0.6)",
          border: "1px solid rgba(22,255,122,0.4)",
        }}
      />
      {confrontos.length > 1 && (
        <span className="ml-1 text-[10px] font-bold tabular-nums" style={{ color: "#16FF7A" }}>
          ×{confrontos.length}
        </span>
      )}
      {open &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed z-[1000] rounded-sm shadow-2xl"
            style={{
              top: pos.top,
              left: pos.left,
              width: 340,
              background: "#111111",
              border: "1px solid rgba(22,255,122,0.35)",
              padding: "10px 12px 12px",
            }}
            data-testid="confronto-popup"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: "#16FF7A" }}>
                Confronto Prévio
              </div>
              <button
                type="button"
                className="text-white/60 hover:text-white p-0.5"
                onClick={() => setOpen(false)}
                aria-label="Fechar"
                data-testid="confronto-popup-close"
              >
                <X size={14} />
              </button>
            </div>
            <div className="space-y-3">
              {confrontos.map((c) => (
                <div key={c.category_id} data-testid={`confronto-cat-${c.category_id}`}>
                  <div className="text-[11px] uppercase tracking-widest text-white/60 mb-1 truncate" title={c.categoria}>
                    {c.categoria}
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-widest text-white/40">
                        <th className="text-left font-semibold pb-1">Atleta</th>
                        <th className="text-right font-semibold pb-1 w-24">Colocação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.positions.map((p) => {
                        const me = p.athlete_id === currentAthleteId;
                        return (
                          <tr
                            key={p.athlete_id}
                            className="border-t border-white/5"
                            data-testid={`confronto-row-${p.athlete_id}`}
                            style={{ background: me ? "rgba(22,255,122,0.07)" : "transparent" }}
                          >
                            <td className="py-1 pr-2 truncate max-w-[200px]" style={{ color: me ? "#fff" : "rgba(255,255,255,0.85)", fontWeight: me ? 700 : 400 }}>
                              {p.nome}
                            </td>
                            <td className="py-1 text-right">
                              <span
                                className="inline-block min-w-[2.4rem] text-center font-display font-bold text-sm px-1.5 py-0.5 rounded-sm"
                                style={{
                                  color: p.posicao <= 3 ? "#0A0A0A" : posColor(p.posicao),
                                  background: p.posicao <= 3 ? posColor(p.posicao) : "rgba(255,255,255,0.06)",
                                }}
                                data-testid={`confronto-pos-${p.athlete_id}`}
                              >
                                {p.posicao}º
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </span>
  );
}
